import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Anchor,
  Box,
  ChevronRightIcon,
  LinkIcon,
  NavigationToggler,
  Option,
  RawButton,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import routes from '@/common/routes';
import { RMR_SHOP_LINK } from '@/modules/core/components/Menu/menu.types';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { useOrganizationProperties } from '@/modules/transaction';
import { ManageProfileTabs } from '@/modules/user/components/ManageProfile/manageProfile.types';
import theme from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { TAB_LINK } from '../useRedirectToManageProfileTab';

const DEFAULT_TAB = ManageProfileTabs.COMMUNICATION_PREFERENCES;
const isRmrTheme = whiteLabelingConfig.key === THEMES.rmr;

interface Props {
  tab: string;
  onSetTab: (tab: string) => void;
  hasBeneficiariesModule?: boolean;
  isLoading?: boolean;

  onSetMobileModal?: (isMobileModal: boolean) => void;
}
const ManageProfileTabsToggle: React.FC<Props> = ({
  hasBeneficiariesModule,
  isLoading,
  tab,
  onSetTab,
  onSetMobileModal,
}) => {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const defaultTabValueFromURL = useMemo(
    () => new URLSearchParams(location?.search || '').get(TAB_LINK),
    [location]
  );
  const { organizationProperties } = useOrganizationProperties();
  const [currentTab, setCurrentTab] = useState<string>(defaultTabValueFromURL || DEFAULT_TAB);
  const history = useHistory();
  useEffect(() => {
    if (currentTab) {
      onSetTab(currentTab);
      if (isMobile && currentTab !== DEFAULT_TAB && onSetMobileModal) {
        onSetMobileModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);
  useEffect(() => {
    if (defaultTabValueFromURL === ManageProfileTabs.BENEFICIARIES && !hasBeneficiariesModule && !isLoading) {
      history.push(routes.MANAGE_PROFILE);
      setCurrentTab(DEFAULT_TAB);
    }
  }, [history, defaultTabValueFromURL, hasBeneficiariesModule, setCurrentTab, isLoading]);

  const isLimitedAccess = useHasAccess([{ role: Roles.employeeLimitedAccess }]);
  const { t } = useTranslation();
  useLayoutEffect(() => {
    if (isLimitedAccess) {
      setCurrentTab(ManageProfileTabs.SECURITY);
    }
  }, [isLimitedAccess]);
  const handleOpenMobileModal = useCallback(
    (key: string) => () => {
      onSetTab(key);
      if (onSetMobileModal) {
        onSetMobileModal(true);
      }
    },
    [onSetMobileModal, onSetTab]
  );
  const hasHelpPresent = useMemo(() => {
    return (
      !!organizationProperties?.customerServiceUrlLink ||
      !!organizationProperties?.customerServiceEmail ||
      !!organizationProperties.customerServiceIsEnabled
    );
  }, [
    organizationProperties?.customerServiceEmail,
    organizationProperties.customerServiceIsEnabled,
    organizationProperties?.customerServiceUrlLink,
  ]);
  const options = useMemo<Option[]>(
    () =>
      isLimitedAccess
        ? [
            {
              key: ManageProfileTabs.SECURITY,
              value: ManageProfileTabs.SECURITY,
              title: t('Security'),
            },
          ]
        : [
            {
              key: ManageProfileTabs.COMMUNICATION_PREFERENCES,
              value: ManageProfileTabs.COMMUNICATION_PREFERENCES,
              title: t('Communication Preferences'),
            },
            {
              key: ManageProfileTabs.REIMBURSEMENT_OPTIONS,
              value: ManageProfileTabs.REIMBURSEMENT_OPTIONS,
              title: t('Reimbursement Options'),
            },
            {
              key: ManageProfileTabs.DEPENDENTS,
              value: ManageProfileTabs.DEPENDENTS,
              title: t('Dependents'),
            },
            ...(hasBeneficiariesModule
              ? [
                  {
                    key: ManageProfileTabs.BENEFICIARIES,
                    value: ManageProfileTabs.BENEFICIARIES,
                    title: t('Beneficiaries'),
                  },
                ]
              : []),
            {
              key: ManageProfileTabs.SECURITY,
              value: ManageProfileTabs.SECURITY,
              title: t('Security'),
            },

            ...(hasHelpPresent && isMobile
              ? [
                  {
                    key: ManageProfileTabs.HELP,
                    value: ManageProfileTabs.HELP,
                    title: t('Help'),
                  },
                ]
              : []),
          ],
    [isLimitedAccess, t, hasBeneficiariesModule, hasHelpPresent, isMobile]
  );

  if (isMobile) {
    return (
      <div>
        <Box
          data-testid="manage-profile-tabs-toggle_mobile"
          background="module"
          pad="spacing16"
          gap="spacing8"
          round="container3Round"
          margin={{ top: 'spacing16' }}
        >
          {options.map((item) => (
            <RawButton key={item.key} onClick={handleOpenMobileModal(item.key as string)}>
              <Box
                data-testid={`manage-profile-tab_${item.key}`}
                direction="row"
                align="center"
                background="canvas"
                justify="between"
                pad="spacing16"
                height="64px"
                round="checkboxRound"
                border={{ size: '1px', color: 'border2' }}
              >
                <Text size="medium" weight={400} truncate title={item.title}>
                  {item.title}
                </Text>
                <Box width="24px" height="24px" align="center" justify="center">
                  <ChevronRightIcon size="14px" />
                </Box>
              </Box>
            </RawButton>
          ))}

          {isRmrTheme && isMobile && (
            <Anchor href={RMR_SHOP_LINK} target="_blank" rel="noopener noreferrer">
              <Box
                data-testid="manage-profile-tab_shop-fsa"
                direction="row"
                align="center"
                background="canvas"
                justify="between"
                pad="spacing16"
                height="64px"
                round="checkboxRound"
                border={{ size: '1px', color: 'border2' }}
                width="100%"
              >
                <Text size="medium" weight={400} truncate>
                  Shop FSA Store
                </Text>
                <Box width="24px" height="24px" align="center" justify="center">
                  <LinkIcon size="16px" />
                </Box>
              </Box>
            </Anchor>
          )}
        </Box>
      </div>
    );
  }
  return (
    <Box data-testid="manage-profile-tabs-toggle_desktop" margin={{ top: 'spacing24' }}>
      <NavigationToggler
        value={currentTab}
        onChange={setCurrentTab}
        options={options}
        buttonStyle={{
          padding: '10px 16px',
        }}
        containerStyle={{
          borderRadius: theme.rounds.container1Round,
        }}
      />
    </Box>
  );
};

export default ManageProfileTabsToggle;
